import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  IosShare as PublishIcon,
  Refresh as RotateIcon,
  LinkOff as DisableIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ProductEmbed } from './types';

interface ProductEmbedDialogProps {
  open: boolean;
  productName: string;
  embed: ProductEmbed | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  onClose: () => void;
  onPublish: () => void;
  onDisable: () => void;
  onRotate: () => void;
  onCopyCode: (code: string) => void;
}

export const ProductEmbedDialog: React.FC<ProductEmbedDialogProps> = ({
  open,
  productName,
  embed,
  loading,
  actionLoading,
  error,
  onClose,
  onPublish,
  onDisable,
  onRotate,
  onCopyCode,
}) => {
  const { t } = useTranslation();

  const iframeCode = useMemo(() => {
    if (!embed?.enabled || !embed.embed_path) return '';
    const src = `${window.location.origin}${embed.embed_path}`;
    return `<iframe src="${src}" width="100%" height="600" style="border:0" loading="lazy"></iframe>`;
  }, [embed?.embed_path, embed?.enabled]);

  const published = Boolean(embed?.enabled && embed?.public_key);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack spacing={0.75}>
          <Typography variant="h6" fontWeight={700}>
            {t('productDetails.embedTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {productName}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Stack spacing={2.25}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              color={published ? 'success' : 'default'}
              label={published ? t('productDetails.embedPublished') : t('productDetails.embedNotPublished')}
            />
            {embed?.rotated_at && (
              <Typography variant="caption" color="text.secondary">
                {t('productDetails.embedRotated')}
              </Typography>
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {t('productDetails.embedDescription')}
          </Typography>

          {published ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={iframeCode}
                  label={t('productDetails.embedCodeLabel')}
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title={t('productDetails.copyEmbedCode')}>
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => onCopyCode(iframeCode)}
                      disabled={!iframeCode || actionLoading}
                    >
                      <CopyIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          ) : (
            <Alert severity="info">{t('productDetails.embedPublishPrompt')}</Alert>
          )}

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              {t('productDetails.embedSecurityTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('productDetails.embedSecurityDescription')}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={actionLoading}>
          {t('productDetails.close')}
        </Button>
        {published && (
          <Button startIcon={<DisableIcon />} color="error" onClick={onDisable} disabled={actionLoading}>
            {t('productDetails.embedDisable')}
          </Button>
        )}
        {published && (
          <Button startIcon={<RotateIcon />} onClick={onRotate} disabled={actionLoading}>
            {t('productDetails.embedRotate')}
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={<PublishIcon />}
          onClick={onPublish}
          disabled={actionLoading || loading}
        >
          {published ? t('productDetails.embedRepublish') : t('productDetails.embedPublish')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
